import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Card, CardBody, CardHeader } from "reactstrap";
import moment from "moment-timezone";
import Topbar from "./Topbar";
import Loading from "../../../../components/Loaders/Loading";
import { firstToUpper } from "../../../../utils/helpers/string";

function Chart({
    data,
    visibleActual,
    visibleBudget,
    visibleScheduled,
    onFiltersChange,
    filters,
    setFilters,
    loading,
    group,
    canViewAmount,
    canViewHours,
}) {
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    const chartContainerRef = useRef(null);

    const updateDimensions = useCallback(() => {
        if (chartContainerRef.current) {
            const { offsetWidth, offsetHeight } = chartContainerRef.current;
            setDimensions({
                width: offsetWidth,
                height: offsetHeight,
            });
        }
    }, []);

    useEffect(() => {
        updateDimensions();
        window.addEventListener("resize", updateDimensions);

        return () => window.removeEventListener("resize", updateDimensions);
    }, [updateDimensions]);

    useEffect(() => {
        if (chartContainerRef.current) {
            const resizeObserver = new ResizeObserver(() => {
                updateDimensions();
            });
            resizeObserver.observe(chartContainerRef.current);

            return () => resizeObserver.disconnect();
        }
    }, [updateDimensions]);

    const isAmount = useMemo(() => {
        return filters.type === "amount";
    }, [filters.type]);

    const actualDataset = useMemo(
        () =>
            data
                ?.filter((d) => d.name === "Actual")
                ?.map((item) => {
                    if (isAmount) {
                        return { date: item.date, value: item.amount };
                    }
                    return {
                        date: item.date,
                        value: Math.floor(moment.duration(item.hours, "s").asHours()),
                    };
                }) || [],
        [data, isAmount]
    );

    const budgetDataSet = useMemo(
        () =>
            data
                ?.filter((d) => d.name === "Budget")
                ?.map((item) => {
                    if (isAmount) {
                        return { date: item.date, value: item.amount };
                    }
                    return {
                        date: item.date,
                        value: Math.floor(moment.duration(item.hours, "s").asHours()),
                    };
                }) || [],
        [data, isAmount]
    );

    const scheduledDataSet = useMemo(
        () =>
            data
                ?.filter((d) => d.name === "Scheduled")
                ?.map((item) => {
                    if (isAmount) {
                        return { date: item.date, value: item.amount };
                    }
                    return {
                        date: item.date,
                        value: Math.floor(moment.duration(item.hours, "s").asHours()),
                    };
                }) || [],
        [data, isAmount]
    );

    const mergedData = useMemo(() => {
        return actualDataset.map((actualItem) => {
            const budgetItem = budgetDataSet.find((b) => b.date === actualItem.date) || {};
            const scheduledItem = scheduledDataSet.find((b) => b.date === actualItem.date) || {};
            return {
                date: actualItem.date,
                actual: actualItem.value,
                budget: budgetItem.value || 0,
                scheduled: scheduledItem.value || 0,
            };
        });
    }, [actualDataset, budgetDataSet, scheduledDataSet]);

    return (
        <Card style={{ height: "100%" }}>
            <CardHeader className="py-3">
                <Topbar
                    onFiltersChange={onFiltersChange}
                    setFilters={setFilters}
                    filters={filters}
                    group={group}
                    canViewAmount={canViewAmount}
                    canViewHours={canViewHours}
                />
            </CardHeader>

            <CardBody className="d-flex" style={{ height: "100%" }}>
                {loading ? (
                    <div className="w-100 text-center">
                        <Loading />
                    </div>
                ) : (
                    <div className="flex-grow-1 d-flex flex-column mb-3" ref={chartContainerRef}>
                        <ResponsiveContainer width="100%" height={dimensions.height || '100%'}>
                            <BarChart data={mergedData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar
                                    dataKey="actual"
                                    fill="#010a63"
                                    hide={visibleActual}
                                    name={isAmount ? "Actual amount" : "Actual hours"}
                                />
                                <Bar
                                    dataKey="budget"
                                    fill={isAmount ? "black" : "#5428e0"}
                                    hide={visibleBudget}
                                    name={isAmount ? "Budget amount" : "Budget hours"}
                                />
                                <Bar
                                    dataKey="scheduled"
                                    fill={isAmount ? "#48bbc3" : "#48bbc3"}
                                    hide={visibleScheduled}
                                    name={isAmount ? "Scheduled amount" : "Scheduled hours"}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </CardBody>
        </Card>
    );
}

export default Chart;
